// Pure parsers behind the Forecast Studio "why this number" drawer. Inputs are
// the per-line JSON columns published by ml_models.rs (driver_json,
// backtest_json, evidence_json) which arrive either as parsed objects (legacy
// snapshot rows) or as JSON strings (parquet artifact columns).
import type { QuickBooksFpaMlUserDelta } from '../../../data/ServerApiClient'
import { trimStr } from './cfoFormat'

export type ForecastDriverContribution = {
  key: string
  label: string
  amountImpact: number
}

const parseJsonish = (raw: any): any => {
  if (raw == null) return undefined
  if (typeof raw === 'object') return raw
  try {
    return JSON.parse(String(raw)) ?? undefined
  } catch {
    return undefined
  }
}

const humanizeKey = (key: string): string => trimStr(key).replace(/[_-]+/g, ' ').trim()

export const parseDriverDecomposition = (driverJson: any): ForecastDriverContribution[] => {
  const entries = parseJsonish(driverJson)
  if (!Array.isArray(entries)) return []
  const out: ForecastDriverContribution[] = []
  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') continue
    const key = trimStr(entry.driver_key)
    const label = trimStr(entry.label) || humanizeKey(key)
    if (!key && !label) continue
    const amount = Number(entry.impact_amount)
    out.push({
      key: key || label,
      label,
      amountImpact: Number.isFinite(amount) ? amount : 0,
    })
  }
  return out
}

const MODEL_FAMILY_PLAIN_LANGUAGE: Record<string, string> = {
  seasonal_trend: 'seasonal trend model',
  driver_adjusted: 'driver-linked model',
  cash_timing: 'payment-timing model',
  feedback_adjusted: 'feedback-adjusted model',
  baseline: 'baseline average model',
}

export const modelFamilyPlainLanguage = (modelFamily: string | null | undefined): string => {
  const family = trimStr(modelFamily)
  if (!family) return 'forecast model'
  const known = MODEL_FAMILY_PLAIN_LANGUAGE[family.toLowerCase()]
  if (known) return known
  const humanized = humanizeKey(family).toLowerCase()
  return humanized.endsWith('model') ? humanized : `${humanized} model`
}

export const backtestPlainLanguage = (backtestJson: any): string | null => {
  const backtest = parseJsonish(backtestJson)
  if (!backtest || typeof backtest !== 'object') return null
  const holdoutCount = Math.floor(Number(backtest.holdout_count))
  if (!Number.isFinite(holdoutCount) || holdoutCount <= 0) return null
  if (backtest.mape == null) return null
  const mape = Number(backtest.mape)
  if (!Number.isFinite(mape) || mape < 0) return null
  const pct = mape * 100
  const pctLabel = pct < 0.95 ? '<1%' : `${Math.round(pct)}%`
  return `Historically within ${pctLabel} on this line (${holdoutCount}-month holdout)`
}

export type ForecastReviewState = 'approved' | 'flagged' | 'needs_discussion'

export const FORECAST_REVIEW_STATES: ForecastReviewState[] = ['approved', 'flagged', 'needs_discussion']

const REVIEW_STATE_PRIORITY: Record<ForecastReviewState, number> = {
  flagged: 3,
  needs_discussion: 2,
  approved: 1,
}

export const isForecastReviewState = (value: string | null | undefined): value is ForecastReviewState =>
  FORECAST_REVIEW_STATES.includes(trimStr(value) as ForecastReviewState)

// One badge state per statement node: the highest-priority review state among
// the node's line deltas (flagged > needs_discussion > approved).
export const forecastNodeReviewStates = (
  deltas: Array<Pick<QuickBooksFpaMlUserDelta, 'target_kind' | 'node_id' | 'feedback_state' | 'scenario_key'>> | null | undefined,
  scenarioKey: string,
): Record<string, ForecastReviewState> => {
  const out: Record<string, ForecastReviewState> = {}
  for (const delta of deltas || []) {
    if (delta?.target_kind !== 'line') continue
    if (trimStr(delta.scenario_key || 'base') !== trimStr(scenarioKey || 'base')) continue
    const nodeId = trimStr(delta.node_id)
    const state = trimStr(delta.feedback_state)
    if (!nodeId || !isForecastReviewState(state)) continue
    const current = out[nodeId]
    if (!current || REVIEW_STATE_PRIORITY[state] > REVIEW_STATE_PRIORITY[current]) {
      out[nodeId] = state
    }
  }
  return out
}
